import { createSelector, on } from '@ngrx/store';
import { createSlice, noopReducer, PayloadAction } from 'ngrx-slice';

import { ImageResponse, ImageUpdateRequest } from '@app/api/marketplace/models';
import { ContainersActions } from '@containers/store/containers.slice';
import { GetImageRequest } from '@image-upload/models/get-images-request.interface';
import { ImageCreateRequest } from '@image-upload/models/image-create-request.interface';
import { PackagingActions } from '@packaging/store/packaging.slice';

export interface State {
  images: Array<ImageResponse>;
  hasError: boolean;
  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;
}

export const initialState: State = {
  images: [],
  hasError: false,
  isLoading: false,
  isUploading: false,
  uploadProgress: 0,
};

export const {
  actions: ImageUploadActions,
  selectors,
  ...ImageUploadFeature
} = createSlice({
  name: 'image-upload',
  initialState,
  reducers: {
    upload: {
      trigger: (
        state,
        _: PayloadAction<{ file: File; request: ImageCreateRequest }>
      ): State => ({
        ...state,
        isUploading: true,
      }),
      progress: (
        state: State,
        action: PayloadAction<{ progress: number }>
      ): State => ({
        ...state,
        uploadProgress: action.progress,
      }),
      success: (state, action: PayloadAction<{ image: ImageResponse }>) => {
        state.images = [...state.images, action.image];
        state.hasError = false;
        state.isUploading = false;
        state.uploadProgress = 0;
      },
      failure: (state): State => ({
        ...state,
        hasError: true,
        isUploading: false,
        uploadProgress: 0,
      }),
    },
    loadImages: {
      trigger: (
        state,
        _: PayloadAction<{ request: GetImageRequest }>
      ): State => ({
        ...state,
        isLoading: true,
      }),
      success: (
        state,
        action: PayloadAction<{ images: Array<ImageResponse> }>
      ) => {
        state.images = [...action.images];
        state.hasError = false;
        state.isLoading = false;
      },
      failure: (state): State => ({
        ...state,
        hasError: true,
        isLoading: false,
      }),
    },
    updateImageData: {
      trigger: (
        state,
        _: PayloadAction<{ imageId: string; request: ImageUpdateRequest }>
      ): State => ({
        ...state,
        isLoading: true,
      }),
      success: (state, action: PayloadAction<{ image: ImageResponse }>) => {
        state.images = [
          ...state.images.map((i) => {
            if (i.id === action.image.id) {
              return action.image;
            } else {
              i.isDefault = action.image.isDefault ? false : i.isDefault;
              return i;
            }
          }),
        ];

        state.hasError = false;
        state.isLoading = false;
      },
      failure: (state): State => ({
        ...state,
        hasError: true,
        isLoading: false,
      }),
    },
    deleteImage: {
      trigger: (state, _: PayloadAction<{ imageId: string }>): State => ({
        ...state,
        isLoading: true,
      }),
      success: (state, action: PayloadAction<{ id: string }>) => {
        state.images = [...state.images.filter((i) => i.id !== action.id)];
        state.hasError = false;
        state.isLoading = false;
      },
      failure: (state): State => ({
        ...state,
        hasError: true,
        isLoading: false,
      }),
    },
    clearImageStore: {
      trigger: (state) => {
        state.images = [];
      },
      success: noopReducer(),
    },
  },
  extraReducers: [
    on<State, [typeof ContainersActions.loadContainers.trigger]>(
      ContainersActions.loadContainers.trigger,
      (state) => ({
        ...state,
        temporaryAttachments: [],
        uploadedImages: [],
      })
    ),
    on<State, [typeof PackagingActions.loadPackages.trigger]>(
      PackagingActions.loadPackages.trigger,
      (state) => ({
        ...state,
        temporaryAttachments: [],
        uploadedImages: [],
      })
    ),
  ],
});

export const ImageUploadSelectors = {
  ...selectors,
  hasError: createSelector(selectors.selectHasError, (hasError) => hasError),
  isLoading: createSelector(
    selectors.selectIsLoading,
    (isLoading) => isLoading
  ),
};
